import { useCallback, useEffect, useMemo, useState } from 'react';
import { ApiError, api, type DriverNotificationRow, type DriverSendResult } from '../../lib/api';
import { Drawer } from '@allrouted/ui/Drawer';
import { Field, SettingsCard, TextArea, TextInput, Toggle } from '@allrouted/ui/Field';
import { useUi } from '@allrouted/ui/Toaster';

/**
 * Push notifications to drivers' phones.
 *
 * A card on each driver (whether their app can receive one, what they have
 * been sent) and a drawer for writing one -- to that driver, to a hand-picked
 * few, or to every active driver at once.
 */

export interface NotifiableDriver {
  id: string;
  name: string;
  active: boolean;
  push_devices?: number;
}

const STATUS: Record<DriverSendResult['status'], { tone: 'ok' | 'warn' | 'late' | 'idle'; text: string }> = {
  sent: { tone: 'ok', text: 'Sent' },
  no_device: { tone: 'warn', text: 'App not registered' },
  failed: { tone: 'late', text: 'Failed' },
  not_configured: { tone: 'idle', text: 'Notifications not set up' },
};

const KIND: Record<DriverNotificationRow['kind'], string> = {
  manual: 'Message',
  stop_added: 'Stop added',
  stop_removed: 'Stop removed',
};

const when = (iso: string) =>
  new Date(iso).toLocaleString(undefined, { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit' });

const TITLE_MAX = 80;
const BODY_MAX = 500;

/** One driver's notification state and recent history. */
export function DriverNotifyCard({
  territoryId,
  driver,
  onCompose,
  refreshKey,
}: {
  territoryId: string;
  driver: NotifiableDriver;
  onCompose: () => void;
  /** Bumped after a send, so the history picks it up. */
  refreshKey: number;
}) {
  const [rows, setRows] = useState<DriverNotificationRow[] | null>(null);
  const [configured, setConfigured] = useState(true);

  useEffect(() => {
    let live = true;
    setRows(null);
    api
      .driverNotifications(territoryId, driver.id, 8)
      .then((r) => {
        if (!live) return;
        setRows(r.notifications);
        setConfigured(r.configured);
      })
      .catch(() => live && setRows([]));
    return () => {
      live = false;
    };
  }, [territoryId, driver.id, refreshKey]);

  const devices = driver.push_devices ?? 0;

  return (
    <SettingsCard
      title="Notifications"
      description="Push messages to this driver's phone through the driver app."
      editing={false}
      actions={
        <button type="button" className="btn btn-small" onClick={onCompose}>
          Send a notification
        </button>
      }
    >
      <div className="driver-signin-state">
        {!configured ? (
          <span className="pill" data-tone="idle">
            Not set up for AllRouted yet
          </span>
        ) : devices > 0 ? (
          <span className="pill" data-tone="ok">
            Receiving on {devices} device{devices === 1 ? '' : 's'}
          </span>
        ) : (
          <span className="pill" data-tone="warn">
            App not registered for notifications
          </span>
        )}
      </div>
      {!configured ? (
        <p className="hint">An AllRouted administrator needs to add the OneSignal keys before anything can be sent.</p>
      ) : devices === 0 ? (
        <p className="hint">
          They appear here once {driver.name} signs in to the driver app and allows notifications.
        </p>
      ) : null}

      <h3 className="section-label">Recent</h3>
      {rows === null ? (
        <p className="hint">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="hint">Nothing sent yet.</p>
      ) : (
        <ul className="push-log">
          {rows.map((n) => (
            <li key={n.id}>
              <div className="push-log-head">
                <b>{n.title}</b>
                <span className="pill" data-tone={STATUS[n.status].tone}>
                  {STATUS[n.status].text}
                </span>
              </div>
              <span>{n.body}</span>
              <small>
                {KIND[n.kind]} · {when(n.created_at)}
                {n.sent_by ? ` · ${n.sent_by}` : ''}
                {n.status === 'failed' && n.error ? ` · ${n.error}` : ''}
              </small>
            </li>
          ))}
        </ul>
      )}
    </SettingsCard>
  );
}

/** Write a notification to one driver, a few, or all of them. */
export function SendNotificationDrawer({
  open,
  territoryId,
  drivers,
  preselect,
  onClose,
  onSent,
}: {
  open: boolean;
  territoryId: string;
  drivers: NotifiableDriver[];
  /** The drivers ticked when it opens; null for everyone. */
  preselect: string[] | null;
  onClose: () => void;
  onSent: () => void;
}) {
  const ui = useUi();
  const [all, setAll] = useState(true);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState<DriverSendResult[] | null>(null);

  useEffect(() => {
    if (!open) return;
    setAll(preselect === null);
    setPicked(new Set(preselect ?? []));
    setResults(null);
  }, [open, preselect]);

  const active = useMemo(() => drivers.filter((d) => d.active), [drivers]);
  const recipients = all ? active : drivers.filter((d) => picked.has(d.id));
  const unreachable = recipients.filter((d) => (d.push_devices ?? 0) === 0).length;

  const toggle = (id: string, on: boolean) =>
    setPicked((s) => {
      const next = new Set(s);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });

  const send = useCallback(async () => {
    setSending(true);
    try {
      const r = await api.sendDriverNotification(territoryId, {
        ...(all ? { all: true } : { technicianIds: [...picked] }),
        title: title.trim(),
        body: body.trim(),
      });
      setResults(r.results);
      const missed = r.results.length - r.sent;
      ui.notify(
        r.sent > 0 ? 'ok' : 'error',
        r.sent > 0 ? `Sent to ${r.sent} driver${r.sent === 1 ? '' : 's'}` : 'Nothing was sent',
        missed > 0 ? `${missed} could not be reached; see the list.` : undefined,
      );
      setTitle('');
      setBody('');
      onSent();
    } catch (err) {
      ui.notify('error', 'Could not send', err instanceof ApiError ? err.message : String(err));
    } finally {
      setSending(false);
    }
  }, [territoryId, all, picked, title, body, ui, onSent]);

  const ready = title.trim() && body.trim() && recipients.length > 0;

  return (
    <Drawer
      open={open}
      onClose={onClose}
      title="Send a notification"
      subtitle="Appears on the driver's phone through the driver app."
      footer={
        <>
          <button type="button" className="btn" onClick={onClose} disabled={sending}>
            {results ? 'Done' : 'Cancel'}
          </button>
          <button type="button" className="btn btn-primary" disabled={!ready || sending} onClick={() => void send()}>
            {sending ? <span className="spinner" /> : null}
            Send to {recipients.length} driver{recipients.length === 1 ? '' : 's'}
          </button>
        </>
      }
    >
      {results ? (
        <>
          <h3 className="section-label">Last send</h3>
          <ul className="push-results">
            {results.map((r) => (
              <li key={r.technicianId}>
                <b>{r.name ?? 'Driver'}</b>
                <span className="pill" data-tone={STATUS[r.status].tone}>
                  {STATUS[r.status].text}
                </span>
                {r.status === 'failed' && r.error ? <small>{r.error}</small> : null}
              </li>
            ))}
          </ul>
        </>
      ) : null}

      <Field label="Send to">
        <Toggle checked={all} onChange={setAll} label={all ? `All active drivers (${active.length})` : 'Chosen drivers'} />
      </Field>
      {all ? null : (
        <div className="push-pick">
          {drivers.map((d) => (
            <div key={d.id} className="push-pick-row">
              <Toggle checked={picked.has(d.id)} onChange={(on) => toggle(d.id, on)} label={d.name} />
              {(d.push_devices ?? 0) === 0 ? <small>app not registered</small> : null}
              {d.active ? null : <small>inactive</small>}
            </div>
          ))}
        </div>
      )}

      <Field label="Title" hint={`${title.length}/${TITLE_MAX}`}>
        <TextInput value={title} onChange={(v) => setTitle(v.slice(0, TITLE_MAX))} placeholder="Change to this afternoon" />
      </Field>
      <Field label="Message" wide hint={`${body.length}/${BODY_MAX}`}>
        <TextArea value={body} rows={4} onChange={(v) => setBody(v.slice(0, BODY_MAX))} />
      </Field>
      {unreachable > 0 ? (
        <p className="hint">
          {unreachable} of these driver{unreachable === 1 ? ' has' : 's have'} no device registered and will not
          receive it.
        </p>
      ) : null}
    </Drawer>
  );
}
